import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["value", "inputRef", "onChange", "defaultDate", "defaultTime"];
import I18n from 'I18n';
import { useMemo, useRef } from 'react';
import { TimeInput } from 'foundations-components/transitional/components/TimeInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Box } from 'foundations-components/transitional/components/Box';
import { useProperty } from '../v2/hooks/useProperty';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { fromMoment, toMoment, now } from 'foundations-components/transitional/utils/SimpleDate';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MINUTES_IN_HOUR = 60;
const DEFAULT_TIME_MINUTES = 8 * 60; // 8 AM

/**
 * Attempt to correctly offset the time selection based on changing DST dates. This
 * is only relevant when selecting times on a day where DST starts or ends.
 */
const getMinutesWithDstOffset = (moment, minutes) => {
  const isDst = moment.isDST();
  const isNextDayDst = moment.clone().add(1, 'day').isDST();

  // A best effort to cover the case where DST is ending and we need to bump the time forward
  // by an hour to accommodate. This only needs to occur after 3:00AM.
  if (isDst && !isNextDayDst && minutes >= 180) {
    return minutes + 60;
  }

  // A best effort to cover the case where DST is starting and we need to bump the time back
  // by an hour to accommodate. This only needs to occur after 3:00AM. But, it will leave a somewhat
  // weird hole from 2:00 AM to 3:00AM, where time selections will jump back. For example,
  // selecting 2:30AM will result in 1:30AM. That period of time technically doesn't exist since
  // the clock jumps forward once they reach 2:00AM.
  if (!isDst && isNextDayDst && minutes >= 180) {
    return minutes - 60;
  }
  return minutes;
};
export default function CRMPropertyDateTimeInput(_ref) {
  var _property$label, _property$label2;
  let {
      value,
      inputRef: dateInputRef,
      onChange,
      defaultDate,
      defaultTime
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const property = useProperty();
  const timeInputRef = useRef(null);
  const momentValue = useMemo(() => {
    if (value || value === 0) {
      const timestamp = Number(value);
      if (isNaN(timestamp)) {
        return null;
      }
      return I18n.moment.userTz(timestamp);
    } else {
      return null;
    }
  }, [value]);
  const timeValue = momentValue && momentValue.hours() * MINUTES_IN_HOUR + momentValue.minutes();

  // If available use the selected "date" input value
  // instead of the current date to account for
  // daylight saving time changes.
  const timezoneLabel = momentValue ? momentValue.format('z') : I18n.moment.userTz().format('z');
  const timezoneFormat = `LT [${timezoneLabel}]`;
  const dateValue = momentValue && fromMoment(momentValue);
  const handleDateChange = evt => {
    const date = evt.target.value;

    // If the date is null, we should clear both the date and time values
    if (date === null) {
      if (typeof onChange === 'function') {
        onChange(syntheticEvent(null));
      }
      return;
    }
    const dateMoment = toMoment(date || defaultDate || now());
    const minutes = timeValue !== null ? timeValue : defaultTime || DEFAULT_TIME_MINUTES;
    const timestamp = dateMoment.add(minutes, 'minutes').valueOf();
    const focusDateInput = timeValue === null && dateValue === null;
    const isValueChanged = (value === null || value === void 0 ? void 0 : value.toString()) !== timestamp.toString();
    if (typeof onChange === 'function' && isValueChanged) {
      onChange(syntheticEvent(timestamp));
    }
    if (focusDateInput) {
      var _timeInputRef$current;
      timeInputRef === null || timeInputRef === void 0 || (_timeInputRef$current = timeInputRef.current) === null || _timeInputRef$current === void 0 || _timeInputRef$current.focus();
    }
  };
  const handleTimeChange = evt => {
    const time = evt.target.value;
    const dateMoment = toMoment(dateValue || defaultDate || now());
    const minutes = time !== null ? time : defaultTime || DEFAULT_TIME_MINUTES;
    const timestamp = dateMoment.add(getMinutesWithDstOffset(dateMoment, minutes), 'minutes').valueOf();
    const focusDateInput = timeValue === null && dateValue === null;
    const isValueChanged = (value === null || value === void 0 ? void 0 : value.toString()) !== timestamp.toString();
    if (typeof onChange === 'function' && isValueChanged) {
      onChange(syntheticEvent(timestamp));
    }
    if (focusDateInput) {
      var _dateInputRef$current;
      dateInputRef === null || dateInputRef === void 0 || (_dateInputRef$current = dateInputRef.current) === null || _dateInputRef$current === void 0 || _dateInputRef$current.focus();
    }
  };
  const {
    disabled,
    readOnly,
    error
  } = rest;
  return /*#__PURE__*/_jsxs(TFlex, {
    gap: 8,
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 1,
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": (_property$label = property.label) !== null && _property$label !== void 0 ? _property$label : '',
        children: /*#__PURE__*/_jsx(DateInput, {
          inputRef: dateInputRef
          // HACK: prevent ESC key presses from propagating up and cancelling changes
          // See https://git.hubteam.com/HubSpot/UIComponents/issues/9877
          ,
          onKeyDown: _stopPropagation,
          onKeyPress: _stopPropagation,
          onKeyUp: _stopPropagation,
          disabled: disabled,
          readOnly: readOnly,
          value: dateValue || undefined,
          onChange: handleDateChange
          // Currently, only validations with date are supported
          // When validations errors include time, we may want to show the error on the time input as well
          ,
          error: error
        })
      })
    }), /*#__PURE__*/_jsx(Box, {
      grow: 1,
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": (_property$label2 = property.label) !== null && _property$label2 !== void 0 ? _property$label2 : '',
        children: /*#__PURE__*/_jsx(TimeInput, {
          inputRef: timeInputRef,
          disabled: disabled,
          readOnly: readOnly
          // @ts-expect-error UITimeInput propTypes allow 'null', but TS types do not; see https://git.hubteam.com/HubSpot/UIComponents/issues/10822
          ,
          value: timeValue,
          format: timezoneFormat
          // We use this to remount the component if the the timezone label changes based on date
          ,

          onChange: handleTimeChange
        }, timezoneFormat)
      })
    })]
  });
}
function _stopPropagation(e) {
  e.stopPropagation();
}